import { useEffect, useState, useCallback, useRef } from "react";
import { paymentsService, PaginatedPaymentsResponse } from "../../services/paymentsService";
import { Payment } from "../../utils/database";
import useToken from "../../../../hooks/auth/useToken";

export type PaymentCategory =
  | "today"
  | "all"
  | "pending"
  | "succeeded_payout_pending"
  | "succeeded_payout_failed"
  | "succeeded_payout_success"
  | "failed"
  | "by_date";

interface Props {
  category: PaymentCategory;
  date?: string;
  enabled?: boolean;
  senderCountryId?: number;
  receiverCountryId?: number;
}

const ITEMS_PER_PAGE = 20;

export default function usePaymentsByCategory({
  category,
  date,
  enabled = true,
  senderCountryId,
  receiverCountryId,
}: Props) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();
  const { token } = useToken();
  const hasFetchedRef = useRef(false);

  const getPaymentsByCategory = useCallback(
    async (page?: number, searchTerm?: string) => {
      if (!token || !enabled) {
        setLoading(false);
        return;
      }

      const targetPage = page ?? currentPage;
      const targetSearch = searchTerm !== undefined ? searchTerm : search;
      const skip = (targetPage - 1) * ITEMS_PER_PAGE;
      const pagination = {
        skip,
        limit: ITEMS_PER_PAGE,
        search: targetSearch || undefined,
        senderCountryId,
        receiverCountryId,
      };

      setLoading(true);
      try {
        let result: PaginatedPaymentsResponse;

        switch (category) {
          case "today":
            result = await paymentsService.getTodayPayments(token, pagination);
            break;
          case "all":
            result = await paymentsService.getPayments(token, pagination);
            break;
          case "pending":
            result = await paymentsService.getPendingPayments(token, pagination);
            break;
          case "succeeded_payout_pending":
            result = await paymentsService.getSucceededPayoutPending(token, pagination);
            break;
          case "succeeded_payout_failed":
            result = await paymentsService.getSucceededPayoutFailed(token, pagination);
            break;
          case "succeeded_payout_success":
            result = await paymentsService.getSucceededPayoutSuccess(token, pagination);
            break;
          case "failed":
            result = await paymentsService.getFailedPayments(token, pagination);
            break;
          case "by_date":
            if (date) {
              result = await paymentsService.getPaymentsByDate(token, date, pagination);
            } else {
              result = { payments: [], total: 0, skip: 0, limit: ITEMS_PER_PAGE };
            }
            break;
          default:
            result = await paymentsService.getPayments(token, pagination);
        }

        setPayments(result?.payments || []);
        setTotal(result?.total || 0);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setError(error);
        setLoading(false);
      }
    },
    [token, category, date, enabled, currentPage, search, senderCountryId, receiverCountryId]
  );

  const goToPage = useCallback(
    (page: number) => {
      setCurrentPage(page);
      getPaymentsByCategory(page);
    },
    [getPaymentsByCategory]
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      setSearch(searchTerm);
      setCurrentPage(1);
      getPaymentsByCategory(1, searchTerm);
    },
    [getPaymentsByCategory]
  );

  useEffect(() => {
    if (enabled && token) {
      hasFetchedRef.current = true;
      setCurrentPage(1);
      getPaymentsByCategory(1, "");
    }
  }, [token, category, date, enabled, senderCountryId, receiverCountryId]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  return {
    payments,
    total,
    currentPage,
    totalPages,
    goToPage,
    handleSearch,
    search,
    getPayments: () => getPaymentsByCategory(),
    length: payments.length,
    loading,
    error,
    itemsPerPage: ITEMS_PER_PAGE,
  };
}
